import type { Language, LanguagesResponse } from '@erp/shared';
import { useMemo } from 'react';
import { create } from 'zustand';

import { setRtlLocales } from '../hooks/useDirection.js';

import { api } from './api.js';

// Sensible default so the language switcher always has something before the
// catalog loads; refined by load().
const DEFAULT: LanguagesResponse = {
  catalog: [
    { code: 'en', name: 'English', nativeName: 'English', isRtl: false },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', isRtl: true },
  ],
  enabled: ['en', 'ar'],
  default: 'en',
};

interface LanguagesState {
  data: LanguagesResponse;
  load: () => Promise<void>;
}

/**
 * Tenant language configuration (catalog + enabled set + default), loaded once
 * after auth. Drives the language switcher, the translation editors, and the
 * RTL direction (via setRtlLocales).
 */
export const useLanguagesStore = create<LanguagesState>((set) => ({
  data: DEFAULT,
  load: async () => {
    try {
      const r = await api().get<LanguagesResponse>('/i18n/languages');
      set({ data: r.data });
      setRtlLocales(r.data.catalog.filter((l) => l.isRtl).map((l) => l.code));
    } catch {
      /* not authenticated yet / offline — keep the defaults */
    }
  },
}));

/** Catalog entries the tenant has enabled, in catalog order. */
export function useEnabledLanguages(): Language[] {
  const data = useLanguagesStore((s) => s.data);
  return useMemo(() => {
    const enabled = new Set(data.enabled);
    return data.catalog.filter((l) => enabled.has(l.code));
  }, [data]);
}
